/**
 * PathCourseEditor Component
 *
 * Allows editing the courses in a Learning Path.
 * Supports adding, removing, and reordering courses.
 */

import { useEffect, useState } from 'react';
import { Box, Button, Typography, IconButton, Autocomplete, TextField } from '../ui';
import { DeleteIcon, DragIndicatorIcon } from '../icons';
import { Course, CourseType } from '../../apis/api-client';
import { coursesClient } from '../../apis/client-instances';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Type for path course with basic info
interface PathCourseItem {
  courseId: number;
  courseTitle: string;
  sequenceOrder: number;
}

interface PathCourseEditorProps {
  pathCourses: PathCourseItem[];
  onChange: (courses: PathCourseItem[]) => void;
  currentCourseId?: number; // To exclude the learning path itself from search results
}

interface SortablePathCourseItemProps {
  item: PathCourseItem;
  onRemove: (courseId: number) => void;
}

function SortablePathCourseItem({ item, onRemove }: SortablePathCourseItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `path-course-${item.courseId}`,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 1.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        mb: 1,
      }}
    >
      {/* Drag Handle */}
      <Box
        {...attributes}
        {...listeners}
        sx={{
          cursor: 'grab',
          display: 'flex',
          alignItems: 'center',
          padding: '4px',
          ':active': { cursor: 'grabbing' },
        }}
      >
        <DragIndicatorIcon sx={{ width: '20px', height: '20px', color: 'grey.500' }} />
      </Box>

      {/* Course Title */}
      <Typography variant="body2" sx={{ flex: 1 }}>
        {item.courseTitle}
      </Typography>

      {/* Remove Button */}
      <IconButton size="small" onClick={() => onRemove(item.courseId)} sx={{ color: 'error.main' }}>
        <DeleteIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  );
}

export default function PathCourseEditor({ pathCourses, onChange, currentCourseId }: PathCourseEditorProps) {
  const [searchResults, setSearchResults] = useState<Course[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [localPathCourses, setLocalPathCourses] = useState<PathCourseItem[]>(pathCourses);

  // Sync local state with props
  useEffect(() => {
    setLocalPathCourses(pathCourses);
  }, [pathCourses]);

  // Sensors for drag-and-drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Search for courses
  const handleSearch = async (query: string) => {
    setSearchInput(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      setSearchLoading(true);
      const results = await coursesClient.getCourses(undefined, query);
      // Filter out Learning Path courses and the current course
      const filteredResults = results.filter(
        (course) =>
          course.id !== currentCourseId && (course as any).courseType !== CourseType.LearningPath && !localPathCourses.some((pc) => pc.courseId === course.id)
      );
      setSearchResults(filteredResults);
    } catch (error) {
      console.error('Failed to search courses:', error);
    } finally {
      setSearchLoading(false);
    }
  };

  // Add course to path
  const handleAddCourse = (course: Course | null) => {
    if (!course || !course.id) return;

    const newItem: PathCourseItem = {
      courseId: course.id,
      courseTitle: course.title || '',
      sequenceOrder: localPathCourses.length + 1,
    };

    const updated = [...localPathCourses, newItem];
    setLocalPathCourses(updated);
    onChange(updated);
    setSearchInput('');
    setSearchResults([]);
  };

  // Remove course from path
  const handleRemoveCourse = (courseId: number) => {
    const updated = localPathCourses.filter((pc) => pc.courseId !== courseId).map((pc, index) => ({ ...pc, sequenceOrder: index + 1 }));
    setLocalPathCourses(updated);
    onChange(updated);
  };

  // Handle drag end for reordering
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeId = parseInt(String(active.id).replace('path-course-', ''));
    const overId = parseInt(String(over.id).replace('path-course-', ''));

    const oldIndex = localPathCourses.findIndex((pc) => pc.courseId === activeId);
    const newIndex = localPathCourses.findIndex((pc) => pc.courseId === overId);

    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(localPathCourses, oldIndex, newIndex).map((pc, index) => ({
      ...pc,
      sequenceOrder: index + 1,
    }));

    setLocalPathCourses(reordered);
    onChange(reordered);
  };

  const sortableIds = localPathCourses.map((pc) => `path-course-${pc.courseId}`);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="subtitle2" color="text.secondary">
        Courses in Learning Path ({localPathCourses.length})
      </Typography>

      {/* Course Search */}
      <Autocomplete
        freeSolo
        options={searchResults}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.title || '')}
        value={null}
        inputValue={searchInput}
        onInputChange={(event, value, reason) => {
          // Only search if user is typing, not when clearing after selection
          if (reason === 'input') {
            handleSearch(value);
          } else if (reason === 'clear') {
            setSearchInput('');
            setSearchResults([]);
          }
        }}
        onChange={(event, value) => {
          if (value && typeof value !== 'string') {
            handleAddCourse(value);
          }
        }}
        loading={searchLoading}
        filterOptions={(x) => x} // Disable built-in filtering, we use server-side search
        renderInput={(params) => <TextField {...params} label="Search and add courses" placeholder="Type to search..." size="small" />}
      />

      {/* Course List */}
      {localPathCourses.length > 0 ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
            {localPathCourses
              .sort((a, b) => a.sequenceOrder - b.sequenceOrder)
              .map((item) => (
                <SortablePathCourseItem key={item.courseId} item={item} onRemove={handleRemoveCourse} />
              ))}
          </SortableContext>
        </DndContext>
      ) : (
        <Box
          sx={{
            py: 2,
            px: 2,
            textAlign: 'center',
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            No courses added yet. Search above to add courses to the learning path.
          </Typography>
        </Box>
      )}

      {localPathCourses.length > 0 && (
        <Typography variant="caption" color="text.secondary">
          Drag courses to reorder. Click the trash icon to remove.
        </Typography>
      )}
    </Box>
  );
}
